import { Eye, EyeOff } from "lucide-react";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AccountSummary, Transaction } from "../types";
import { accountsAPI, transactionsAPI } from "../services/api";
import LoadingSkeleton from "../components/LoadingSkeleton";
import PageTransition from "../components/PageTransition";
import SummaryCard from "../components/Dashboard/SummaryCard";
import {
  AccountItem,
  EmptyState,
} from "../components/Dashboard/AccountComponents";
import TodayTransactions from "../components/Dashboard/TodayTransactions";
import AdditionalStats from "../components/Dashboard/AdditionalStats";

const Dashboard: React.FC = () => {
  const [showBalance, setShowBalance] = useState(false);
  // Utility to mask balance except first 2 digits
  function maskBalance(amount: number): string {
    const formatted = amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
    });
    const [intPart, decPart] = formatted.split(".");
    let visible = intPart.slice(0, 2);
    let masked = intPart.slice(2).replace(/[0-9]/g, "x");
    let result = visible + masked;
    return `₹${result}.${decPart ? decPart : "00"}`;
  }

  const [accountSummary, setAccountSummary] = useState<AccountSummary | null>(
    null,
  );
  const [todayTransactions, setTodayTransactions] = useState<Transaction[]>([]);
  const [additionalStats, setAdditionalStats] = useState({
    todayStats: { todayCredits: 0, todayDebits: 0, todayCount: 0 },
    weeklyStats: { weeklyCredits: 0, weeklyDebits: 0, weeklyCount: 0 },
    monthlyStats: { monthlyCredits: 0, monthlyDebits: 0, monthlyCount: 0 },
    averageTransaction: 0,
  });
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (initialLoad) {
          setLoading(true);
        }

        // Get dates for filtering
        const today = new Date();
        const todayStart = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
        );
        const weekStart = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

        // Fetch all data
        const [summary, stats, todayTrans, weeklyTrans, monthlyTrans] =
          await Promise.all([
            accountsAPI.getAccountsSummary(),
            transactionsAPI.getTransactionStats(),
            transactionsAPI.getTransactions({
              startDate: todayStart.toISOString(),
              endDate: today.toISOString(),
              limit: 10,
            }),
            transactionsAPI.getTransactions({
              startDate: weekStart.toISOString(),
              endDate: today.toISOString(),
            }),
            transactionsAPI.getTransactions({
              startDate: monthStart.toISOString(),
              endDate: today.toISOString(),
            }),
          ]);

        setAccountSummary(summary);
        setTodayTransactions(todayTrans.transactions);

        // Calculate additional stats
        const todayCredits = todayTrans.transactions
          .filter((t) => t.type === "credit")
          .reduce((sum, t) => sum + t.amount, 0);
        const todayDebits = todayTrans.transactions
          .filter((t) => t.type === "debit")
          .reduce((sum, t) => sum + t.amount, 0);

        const weeklyCredits = weeklyTrans.transactions
          .filter((t) => t.type === "credit")
          .reduce((sum, t) => sum + t.amount, 0);
        const weeklyDebits = weeklyTrans.transactions
          .filter((t) => t.type === "debit")
          .reduce((sum, t) => sum + t.amount, 0);

        const monthlyCredits = monthlyTrans.transactions
          .filter((t) => t.type === "credit")
          .reduce((sum, t) => sum + t.amount, 0);
        const monthlyDebits = monthlyTrans.transactions
          .filter((t) => t.type === "debit")
          .reduce((sum, t) => sum + t.amount, 0);

        const totalAmount = stats?.totalCredit + stats?.totalDebit || 0;
        const averageTransaction =
          totalAmount / (stats?.totalTransactions || 1);

        setAdditionalStats({
          todayStats: {
            todayCredits,
            todayDebits,
            todayCount: todayTrans.transactions.length,
          },
          weeklyStats: {
            weeklyCredits,
            weeklyDebits,
            weeklyCount: weeklyTrans.transactions.length,
          },
          monthlyStats: {
            monthlyCredits,
            monthlyDebits,
            monthlyCount: monthlyTrans.transactions.length,
          },
          averageTransaction,
        });
      } catch (err: any) {
        setError(
          err.response?.data?.message || "Failed to load dashboard data",
        );
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };

    fetchData();
  }, [initialLoad]);

  if (loading && initialLoad) {
    return (
      <PageTransition>
        <div className="p-6">
          <div className="mb-8 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-64"></div>
          </div>

          {/* Stats cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 animate-pulse"
              >
                <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
                <div className="h-8 bg-gray-200 rounded w-20 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>

          <LoadingSkeleton variant="card" count={4} />
        </div>
      </PageTransition>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-danger-50 border border-danger-500 text-danger-600 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <PageTransition>
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Overview of your financial accounts</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[
            {
              label: "Total Balance",
              value: (
                <span className="text-teal-600 flex items-center gap-2">
                  {showBalance
                    ? `₹${(accountSummary?.totalBalance ?? 0).toLocaleString(
                        "en-IN",
                        { minimumFractionDigits: 2 },
                      )}`
                    : maskBalance(accountSummary?.totalBalance ?? 0)}
                  <button
                    type="button"
                    aria-label={showBalance ? "Hide balance" : "Show balance"}
                    className="focus:outline-none ml-1"
                    onClick={() => setShowBalance((v) => !v)}
                  >
                    {showBalance ? (
                      <EyeOff className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </span>
              ),
              icon: (
                <svg
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" />
                </svg>
              ),
              colorClass: "bg-teal-500",
            },
            {
              label: "Bank Accounts",
              value: accountSummary?.accountCount || 0,
              icon: (
                <svg
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
              ),
              colorClass: "bg-primary-500",
            },
            {
              label: "Total Transactions",
              // value: transactionStats?.totalTransactions || 0,
              value: <span className="text-gray-500">N/A for now</span>,
              icon: (
                <svg
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                    clipRule="evenodd"
                  />
                </svg>
              ),
              colorClass: "bg-success-500",
            },
          ].map((card, idx) => (
            <SummaryCard
              key={card.label}
              icon={card.icon}
              label={card.label}
              value={card.value}
              colorClass={card.colorClass}
            />
          ))}
        </div>

        {/* Bank Accounts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-10 rounded-bl-full"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Bank Accounts
                </h3>
                <Link
                  to="/accounts"
                  className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                >
                  Manage →
                </Link>
              </div>
              {accountSummary?.accounts &&
              accountSummary.accounts.length > 0 ? (
                <div className="space-y-3">
                  {accountSummary.accounts.map((account) => (
                    <AccountItem
                      key={account.id}
                      id={account.id}
                      bankName={account.bankName}
                      accountNumber={account.accountNumber}
                      balance={maskBalance(account.balance)}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  message="No bank accounts added yet"
                  actionText="Add your first account"
                  actionLink="/accounts"
                />
              )}
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-indigo-400 to-purple-500 opacity-10 rounded-bl-full"></div>
            <div className="relative">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Quick Actions
              </h3>
              <div className="space-y-3">
                <Link
                  to="/transactions?action=add"
                  className="block w-full bg-primary-500 hover:bg-primary-600 text-white text-center py-3 px-4 rounded-lg transition-colors font-medium"
                >
                  Add Transaction
                </Link>
                <Link
                  to="/accounts?action=add"
                  className="block w-full bg-teal-500 hover:bg-teal-600 text-white text-center py-3 px-4 rounded-lg transition-colors font-medium"
                >
                  Add Account
                </Link>
                <Link
                  to="/transactions"
                  className="block w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-center py-3 px-4 rounded-lg transition-colors font-medium"
                >
                  View All Transactions
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Today's Transaction History */}
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 mb-8">
          <div className="lg:col-span-2">
            <TodayTransactions
              transactions={todayTransactions}
              todayStats={additionalStats.todayStats}
            />
          </div>
        </div>

        {/* Additional Statistics */}
        <AdditionalStats
          weeklyStats={additionalStats.weeklyStats}
          monthlyStats={additionalStats.monthlyStats}
          averageTransaction={additionalStats.averageTransaction}
        />
      </div>
    </PageTransition>
  );
};

export default Dashboard;
